import { Controller } from "@hotwired/stimulus"

// Companion to the `dropdown` controller (tailwindcss-stimulus-components).
//
// While the menu is open, this pins it with position:fixed and tracks the
// button, so the menu escapes an ancestor's overflow clipping — e.g. a
// horizontally scrollable table — instead of being cut off.
//
// The menu stays a DOM child of the dropdown wrapper (we only override its
// inline position), so the dropdown controller's click-outside detection
// (`this.element.contains(target)`) keeps working. Visibility is still owned by
// the dropdown controller, which toggles the `hidden` class; we watch for that.
export default class extends Controller {
  static values = { align: { type: String, default: "left" } }

  connect() {
    this.button = this.element.querySelector('[data-dropdown-target="button"]')
    this.menu = this.element.querySelector('[data-dropdown-target="menu"]')
    if (!this.button || !this.menu) return

    this.reposition = this.reposition.bind(this)
    this.observer = new MutationObserver(() => this.onMenuClassChange())
    this.observer.observe(this.menu, { attributes: true, attributeFilter: ["class"] })
  }

  disconnect() {
    this.observer?.disconnect()
    this.stopTracking()
  }

  onMenuClassChange() {
    if (this.menu.classList.contains("hidden")) {
      this.stopTracking()
    } else {
      this.reposition()
      // Capture phase so scrolling inside any container (the table) is caught.
      window.addEventListener("scroll", this.reposition, true)
      window.addEventListener("resize", this.reposition)
    }
  }

  reposition() {
    const rect = this.button.getBoundingClientRect()
    const left = this.alignValue === "right" ? rect.right - this.menu.offsetWidth : rect.left

    Object.assign(this.menu.style, {
      left: `${Math.max(8, left)}px`,
      margin: "0",
      position: "fixed",
      top: `${rect.bottom + 4}px`,
      // Below the sticky top app bar (z-30 in company_admin.html.erb) so the
      // menu tucks under the customer search / system-admin notification.
      zIndex: "20",
    })
  }

  stopTracking() {
    window.removeEventListener("scroll", this.reposition, true)
    window.removeEventListener("resize", this.reposition)
  }
}
